import axiosInstance from "./AxiosInstance";

const DepositorsService = {
	// 13. DOWNLOAD KYC FILE
	downloadKycFile: async (depositorId, companyKycId) => {
		try {
			const response = await axiosInstance.get(
				`/depositors/${depositorId}/kyc-download/${companyKycId}`,
				{
					responseType: "blob",
				}
			);
			return { success: response.data.success, data: response.data.data };
		} catch (error) {
			return {
				success: false,
				message: error.response?.data?.message || "Failed to download file",
			};
		}
	},
	// 1. CREATE DEPOSITOR
	createDepositor: async (depositorData) => {
		try {
			const response = await axiosInstance.post("/depositors", depositorData);
			console.log('Raw axios response:', response);
			console.log('Response status:', response.status);
			console.log('Response data:', response.data);
			
			return {
				success: response.data.success,
				data: response.data.data,
				message: response.data.message,
			};
		} catch (error) {
			console.error('Create depositor error:', error);
			console.log('Error response:', error.response);
			
			return {
				success: false,
				message: error.response?.data?.message || "Failed to create depositor",
				errors: error.response?.data?.errors || [],
			};
		}
	},

	// 2. GET ALL DEPOSITORS
	getAllDepositors: async (params = {}) => {
		try {
			const queryParams = new URLSearchParams();

			// Add pagination
			if (params.page) queryParams.append("page", params.page);
			if (params.limit) queryParams.append("limit", params.limit);

			// Add filters
			if (params.search) queryParams.append("search", params.search);
			if (params.status) queryParams.append("status", params.status);
			if (params.branchId) queryParams.append("branchId", params.branchId);
			if (params.companyId) queryParams.append("companyId", params.companyId);

			const response = await axiosInstance.get(
				`/depositors?${queryParams.toString()}`
			);
			return {
				success: response.data.success,
				data: response.data.data.data,
				pagination: response.data.data.pagination,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message: error.response?.data?.message || "Failed to fetch depositors",
				data: [],
				pagination: {},
			};
		}
	},

	// Legacy method name for backward compatibility
	getDepositors: async (params = {}) => {
		return await DepositorsService.getAllDepositors(params);
	},

	// 3. GET DEPOSITOR BY ID
	getDepositorById: async (id) => {
		try {
			const response = await axiosInstance.get(`/depositors/${id}`);
			return {
				success: response.data.success,
				data: response.data.data,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message: error.response?.data?.message || "Failed to fetch depositor",
				data: null,
			};
		}
	},

	// 4. UPDATE DEPOSITOR
	updateDepositor: async (id, depositorData) => {
		try {
			const response = await axiosInstance.put(
				`/depositors/${id}`,
				depositorData
			);
			return {
				success: response.data.success,
				data: response.data.data,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message: error.response?.data?.message || "Failed to update depositor",
				errors: error.response?.data?.errors || [],
			};
		}
	},

	// 5. CLOSE DEPOSITOR
	closeDepositor: async (id) => {
		try {
			const response = await axiosInstance.put(`/depositors/${id}/close`);
			return {
				success: response.data.success,
				data: response.data.data,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message: error.response?.data?.message || "Failed to close depositor",
			};
		}
	},

	// 6. GET KYC REQUIREMENTS
	getKycRequirements: async (id) => {
		try {
			const response = await axiosInstance.get(
				`/depositors/${id}/kyc-requirements`
			);
			return {
				success: response.data.success,
				data: response.data.data,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message:
					error.response?.data?.message || "Failed to fetch KYC requirements",
				data: [],
			};
		}
	},

	// 7. SUBMIT KYC DOCUMENT
	submitKycDocument: async (id, kycData, isFileUpload = false) => {
		try {
			let response;

			if (isFileUpload) {
				// For file uploads, use multipart/form-data
				response = await axiosInstance.post(
					`/depositors/${id}/kyc-submit`,
					kycData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					}
				);
			} else {
				// For text submissions, use application/json
				response = await axiosInstance.post(
					`/depositors/${id}/kyc-submit`,
					kycData
				);
			}

			return {
				success: response.data.success,
				data: response.data.data,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message:
					error.response?.data?.message || "Failed to submit KYC document",
				errors: error.response?.data?.errors || [],
			};
		}
	},

	// 8. GET KYC STATUS
	getKycStatus: async (id) => {
		try {
			const response = await axiosInstance.get(`/depositors/${id}/kyc-status`);
			return {
				success: response.data.success,
				data: response.data.data.kycStatus,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message: error.response?.data?.message || "Failed to fetch KYC status",
				data: null,
			};
		}
	},

	// 9. REFRESH DEPOSITOR STATUS
	refreshDepositorStatus: async (id) => {
		try {
			const response = await axiosInstance.put(
				`/depositors/${id}/refresh-status`
			);
			return {
				success: response.data.success,
				data: response.data.data,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message:
					error.response?.data?.message || "Failed to refresh depositor status",
			};
		}
	},

	// 10. BULK REFRESH ALL STATUSES
	refreshAllStatuses: async () => {
		try {
			const response = await axiosInstance.post(
				"/depositors/refresh-all-statuses"
			);
			return {
				success: response.data.success,
				data: { updatedCount: response.data.data.updatedCount },
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message:
					error.response?.data?.message || "Failed to refresh all statuses",
			};
		}
	},

	// 11. GET DEPOSIT HISTORY
	getDepositHistory: async (params = {}) => {
		try {
			const queryParams = new URLSearchParams();

			// Required parameter
			if (params.depositorId)
				queryParams.append("depositorId", params.depositorId);

			// Optional parameters
			if (params.branchId) queryParams.append("branchId", params.branchId);
			if (params.type) queryParams.append("type", params.type);
			if (params.startDate) queryParams.append("startDate", params.startDate);
			if (params.endDate) queryParams.append("endDate", params.endDate);
			if (params.page) queryParams.append("page", params.page);
			if (params.limit) queryParams.append("limit", params.limit);

			const response = await axiosInstance.get(
				`/depositors/deposit-history?${queryParams.toString()}`
			);
			return {
				success: response.data.success,
				data: response.data.data.data,
				pagination: response.data.data.pagination,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message:
					error.response?.data?.message || "Failed to fetch deposit history",
				data: [],
				pagination: {},
			};
		}
	},

	// 12. GET DEPOSITOR HISTORY
	getDepositorHistory: async (id) => {
		try {
			const response = await axiosInstance.get(`/depositors/${id}/history`);
			return {
				success: response.data.success,
				data: response.data.data.history,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message:
					error.response?.data?.message || "Failed to fetch depositor history",
				data: [],
			};
		}
	},

	// 14. GET DEPOSITOR DROPDOWN
	getDepositorsDropdown: async (companyId) => {
		try {
			const response = await axiosInstance.get(`/depositors/dropdown?companyId=${companyId}`);
			return {
				success: response.data.success,
				data: response.data.data,
				message: response.data.message,
			};
		} catch (error) {
			return {
				success: false,
				message:
					error.response?.data?.message || "Failed to fetch branch dropdown",
				data: [],
			};
		}
	},

	// Helper method for search/filter operations
	searchDepositors: async (searchTerm) => {
		return await DepositorsService.getAllDepositors({
			search: searchTerm,
			limit: 50,
		});
	},

	// Helper method for status filtering
	getDepositorsByStatus: async (status) => {
		return await DepositorsService.getAllDepositors({ status });
	},

	// Helper method for branch filtering
	getDepositorsByBranch: async (branchId) => {
		return await DepositorsService.getAllDepositors({ branchId });
	},

	// Legacy methods for backward compatibility
	getDepositorKyc: async (depositorId) => {
		return await DepositorsService.getKycRequirements(depositorId);
	},

	updateDepositorKyc: async (depositorId, kycData) => {
		return await DepositorsService.submitKycDocument(depositorId, kycData);
	},

	// Get depositors statistics
	getDepositorsStatistics: async (params = {}) => {
		try {
			const queryParams = new URLSearchParams();
			if (params.companyId) queryParams.append('companyId', params.companyId);
			if (params.branchId) queryParams.append('branchId', params.branchId);

			const url = `/statistics/depositors${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
			const response = await axiosInstance.get(url);
			
			return {
				success: response.data.success,
				data: response.data.data,
				message: response.data.message
			};
		} catch (error) {
			return {
				success: false,
				message: error.response?.data?.message || 'Failed to fetch depositors statistics',
				data: {
					active_count: 0,
					inactive_count: 0,
					pending_count: 0,
					total_count: 0
				}
			};
		}
	}
};

export default DepositorsService;
